/** Pixel dimensions of the badge glyphs — must stay in sync with the
 *  `width=`/`height=` attributes on the SVGs inside `FloorAlertBadge.tsx`
 *  and `FloorHoistBadge.tsx`. These mirror trusstor's `SAFETY_ICON_*`
 *  and `ELEVATOR_ICON_*` in `constants/constants.ts`. */
export const ALERT_ICON_SIZE = 16;
export const HOIST_ICON_SIZE = 25;

/** Natural rendered height (px) of a slab profession chip — the ~18px pill
 *  plus 1px breathing room. The chip-scale divides the measured floor pitch
 *  by this to decide how far to shrink chips so they fit their floor band. */
export const PILL_NATURAL_PX = 19;
/** Floor for `chipScale`: never shrink chips below half their design size —
 *  below ~9px the count + label become illegible, so we'd rather let very
 *  cramped towers overlap slightly than render unreadable chips. */
export const MIN_CHIP_SCALE = 0.5;

/** How far to scale a building's slab chips so each fits its floor band: the
 *  median measured slab height (≈ the floor pitch) over a chip's natural
 *  height, never below `MIN_CHIP_SCALE`. Capped at the building's zoom: at 1×
 *  chips never grow past their design size, and zoomed in they grow with the
 *  building instead of staying put. */
export function chipScaleFor(slabHeights: number[], zoom: number): number {
  const heights = slabHeights.filter((height) => height > 0).sort((heightA, heightB) => heightA - heightB);
  const median = heights[Math.floor(heights.length / 2)];
  if (median === undefined) return zoom;
  return Math.min(zoom, Math.max(MIN_CHIP_SCALE, median / PILL_NATURAL_PX));
}

/** Horizontal gap (px) between chips in a floor row — must match the
 *  `gap` on `.chipRow`. `ChipRow` counts it when fitting chips. */
export const CHIP_ROW_GAP = 4;
/** Breathing room (px) kept between a floor's chip row and the building's
 *  right edge so the last chip never visually touches the silhouette —
 *  mirrors the `- 10` in trusstor's `ProfessionChipsContainer`. */
export const CHIP_ROW_RIGHT_PAD = 10;

/** The room for a floor's chip row in the cell's own px: the slab marker's
 *  width less the right pad, before the cell's shrink-to-fit scale. */
export function chipRoom(slabWidth: number, scale: number): number {
  return (slabWidth - CHIP_ROW_RIGHT_PAD) / scale;
}
/** Max-width (px) cap for a slab profession chip — the single source of truth
 *  for the cap, set inline (narrowed to the row's room when that is less). */
export const PROF_CHIP_MAX_PX = 180;
/** Max-width (px) cap for a slab manager chip — see `PROF_CHIP_MAX_PX`. */
export const MANAGER_CHIP_MAX_PX = 130;

/**
 * How many of a row's chips, kept in order, fit `available` px. Once one
 * doesn't, the "+N" tag's width is reserved too. At least one chip always
 * shows — its own max-width truncates it to the row.
 */
export function fitChipCount(widths: readonly number[], available: number, moreWidth: number): number {
  let used = 0;
  for (const [index, width] of widths.entries()) {
    used += (index === 0 ? 0 : CHIP_ROW_GAP) + width;
    const reserve = index < widths.length - 1 ? CHIP_ROW_GAP + moreWidth : 0;
    if (used + reserve > available) return Math.max(1, index);
  }
  return widths.length;
}

export type Anchor = {
  floorId: string;
  left: number;
  top: number;
  width: number;
  height: number;
};

const anchorKey = (anchor: Anchor) => `${anchor.floorId}:${anchor.left}:${anchor.top}:${anchor.width}:${anchor.height}`;

/** `next`, unless it holds exactly the anchors `previous` does: a re-measure
 *  that finds nothing moved keeps the old array, so React skips the re-render
 *  of every overlay built from it. */
export function keepIfUnchanged(previous: Anchor[], next: Anchor[]): Anchor[] {
  const before = previous.map(anchorKey);
  const unchanged = before.length === next.length && next.every((anchor, index) => anchorKey(anchor) === before[index]);
  return unchanged ? previous : next;
}

/**
 * Reads the bounding rect of every SVG element whose id starts with `prefix`,
 * expressed in the host container's pixel coordinate system. Trusstor SVGs
 * stamp `floor<N>` and `chip<N>` markers (often invisible `<rect fill="none">`
 * shapes wrapped in a `<g>`) at fixed positions; we measure those markers
 * post-layout to position HTML overlays at the same spots. Adobe Illustrator
 * suffixes duplicate ids with `_<n>` — `cleanId()` strips that.
 */
export function readAnchors(container: HTMLElement, prefix: 'floor' | 'chip' | 'alert' | 'elevator'): Anchor[] {
  const containerRect = container.getBoundingClientRect();
  const out: Anchor[] = [];
  container.querySelectorAll<SVGGraphicsElement>(`[id^="${prefix}"]`).forEach((el) => {
    const after = el.id.replace(new RegExp(`^${prefix}`), '');
    const floorId = after.split('_')[0];
    if (!floorId) return;
    const rect = el.getBoundingClientRect();
    if (rect.width === 0 && rect.height === 0) return;
    out.push({
      floorId,
      left: rect.left - containerRect.left,
      top: rect.top - containerRect.top,
      width: rect.width,
      height: rect.height,
    });
  });
  return out;
}
